import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import { FcPlus } from "react-icons/fc";
import { useCreateIndividual } from "../../../graphql/IndividualAPI/IndividualMutation";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useAppThunkDispatch } from "../../../Redux/hooks";
import { AddingNewAdminWithDescription } from "../../../Redux/Actions/AdminActions/AddingNewAdminWithDesc";
import { OwnerType } from "../../../Redux/MasjidTypes";
import AddressInput from "../../Shared/AddressInput/address-input";

interface IndividualFormProps {
  open: boolean;
  onClose: () => void;
}

const IndividualForm: React.FC<IndividualFormProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const { createIndividual, loading: createLoading } = useCreateIndividual();

  // Individual form refs
  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<any>(null);
  const contactRef = useRef<any>(null);
  const individualTypeRef = useRef<HTMLInputElement | null>(null);

  // Form state
  const [isAddressPrivate, setIsAddressPrivate] = useState(false);
  const [addressData, setAddressData] = useState<{
    address: string;
    lat: number;
    lng: number;
    isValid: boolean;
  }>({
    address: "",
    lat: 0,
    lng: 0,
    isValid: false,
  });

  // Error states for individual
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [contactError, setContactError] = useState(false);
  const [individualTypeError, setIndividualTypeError] = useState(false);

  const handleClose = () => {
    // Reset individual form
    setNameError(false);
    setEmailError(false);
    setPhoneError(false);
    setAddressError(false);
    setContactError(false);
    setIndividualTypeError(false);
    setIsAddressPrivate(false);
    setAddressData({
      address: "",
      lat: 0,
      lng: 0,
      isValid: false,
    });

    // Clear individual form fields
    if (nameRef.current) nameRef.current.value = "";
    if (emailRef.current) emailRef.current.value = "";
    if (phoneRef.current) phoneRef.current.value = "";
    if (contactRef.current) contactRef.current.value = "";
    if (individualTypeRef.current) individualTypeRef.current.value = "";

    onClose();
  };

  const validateForm = () => {
    let isValid = true;

    // Validate individual required fields
    if (!nameRef.current?.value.trim()) {
      setNameError(true);
      isValid = false;
    } else {
      setNameError(false);
    }

    if (!emailRef.current?.value.trim()) {
      setEmailError(true);
      isValid = false;
    } else {
      setEmailError(false);
    }

    if (!phoneRef.current?.value) {
      setPhoneError(true);
      isValid = false;
    } else {
      setPhoneError(false);
    }

    if (!addressData.address.trim() || !addressData.isValid) {
      setAddressError(true);
      isValid = false;
    } else {
      setAddressError(false);
    }

    if (!individualTypeRef.current?.value.trim()) {
      setIndividualTypeError(true);
      isValid = false;
    } else {
      setIndividualTypeError(false);
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Create individual
      const coordinates = [addressData.lng, addressData.lat];

      const individualData = {
        name: nameRef.current?.value || "",
        address: addressData.address,
        location: {
          type: "Point",
          coordinates: coordinates as [number, number],
        },
        contact: contactRef.current?.value?.replace(/\s+/g, "") || "",
        individualType: individualTypeRef.current?.value || "",
        externalLinks: [
          {
            name: "Email",
            url: emailRef.current?.value || "",
          },
        ],
        isAddressPrivate,
      };

      const individualResponse = await createIndividual(individualData);

      if (individualResponse?._id) {
        // Register user with individual ID as masjidId
        const userData = {
          name: nameRef.current?.value || "",
          email: emailRef.current?.value.toLowerCase() || "",
          role: "subadmin", // Hardcoded as requested
          contact: phoneRef.current?.value?.replace(/\s+/g, "") || "",
          description: `User for individual: ${nameRef.current?.value}`,
          ownerType: OwnerType.INDIVIDUAL, // Automatically set for individuals
          masjidId: individualResponse._id, // Pass individual ID as masjidId
        };

        const userResponse = await dispatch(
          AddingNewAdminWithDescription(userData)
        );

        if (userResponse?._id) {
          toast.success("Individual and user account created successfully");
          setTimeout(() => {
            navigate("/individuals");
          }, 1000);
          handleClose();
        } else {
          toast.error("Individual created but user account creation failed");
        }
      }
    } catch (error: any) {
      console.error("Error creating individual:", error);
      toast.error(
        `Error creating individual: ${error.message || "Unknown error"}`
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "22px",
          padding: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          color: "#1B8368",
          fontSize: "20px",
          fontWeight: "600",
          padding: "20px",
        }}
      >
        Add New Individual
      </DialogTitle>
      <DialogContent sx={{ padding: "20px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 3,
          }}
        >
          <TextField
            inputRef={nameRef}
            label="Full Name"
            placeholder="Enter full name"
            fullWidth
            error={nameError}
            helperText={nameError ? "Full name is required" : ""}
            sx={{ marginBottom: 2 }}
          />

          <TextField
            inputRef={emailRef}
            label="Email"
            placeholder="Enter email address"
            type="email"
            fullWidth
            error={emailError}
            helperText={emailError ? "Email is required" : ""}
            sx={{ marginBottom: 2 }}
          />

          <Box sx={{ gridColumn: "span 2", marginBottom: 2 }}>
            <PhoneInput
              ref={phoneRef}
              international
              countryCallingCodeEditable={false}
              defaultCountry="US"
              placeholder="Enter phone number"
              onChange={() => {}} // Required prop
              style={{
                width: "100%",
                height: "56px",
                border: phoneError ? "2px solid #d32f2f" : "1px solid #ccc",
                borderRadius: "4px",
                padding: "8px 12px",
              }}
            />
            {phoneError && (
              <div
                style={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  marginTop: "4px",
                }}
              >
                Phone number is required
              </div>
            )}
          </Box>

          <TextField
            inputRef={individualTypeRef}
            label="Individual Type"
            placeholder="Enter individual type (e.g., Scholar, Imam, etc.)"
            fullWidth
            error={individualTypeError}
            helperText={
              individualTypeError ? "Individual type is required" : ""
            }
            sx={{ marginBottom: 2 }}
          />

          <Box
            sx={{
              gridColumn: "span 2",
              marginBottom: 2,
              position: "relative",
              zIndex: 1000,
            }}
          >
            <AddressInput
              value={addressData.address}
              onChange={(data) => {
                setAddressData(data);
                setAddressError(false);
              }}
              label="Address"
              placeholder="Enter or search address"
              error={addressError}
              required={true}
              borderRadius="md"
              height="md"
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={isAddressPrivate}
                onChange={(e) => setIsAddressPrivate(e.target.checked)}
                sx={{
                  color: "#1B8368",
                  "&.Mui-checked": {
                    color: "#1B8368",
                  },
                }}
              />
            }
            label="Keep address private"
            sx={{ gridColumn: "span 2", marginBottom: 2 }}
          />

          <Box sx={{ gridColumn: "span 2", marginBottom: 2 }}>
            <PhoneInput
              ref={contactRef}
              international
              countryCallingCodeEditable={false}
              defaultCountry="US"
              placeholder="Enter contact number (optional)"
              onChange={() => {}} // Required prop
              style={{
                width: "100%",
                height: "56px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "8px 12px",
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        <Button
          onClick={handleClose}
          sx={{
            color: "#666",
            border: "1px solid #ccc",
            "&:hover": {
              border: "1px solid #999",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={createLoading}
          sx={{
            backgroundColor: "#1B8368",
            color: "white",
            "&:hover": {
              backgroundColor: "#156B52",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
          startIcon={
            createLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FcPlus />
            )
          }
        >
          {createLoading ? "Creating..." : "Create Individual"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndividualForm;
